import { CheckCircleOutline, ReportProblemOutlined } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { BodyText } from '@/components/BodyText';
import { FailureKind, useGetRecentFailures } from '@/hooks/api/assets/useGetRecentFailures';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { DashboardTile } from './DashboardTile';
import { FailureKindSummary, FailureSummaryTable } from './FailureSummaryTable';
import { TileEmptyState } from './TileEmptyState';

const FAILURE_KINDS: { kind: FailureKind; type: string; workflow: string }[] = [
  { kind: 'asset', type: 'Asset', workflow: 'Ingestion' },
  { kind: 'report', type: 'Report', workflow: 'Report generation' },
  { kind: 'part-finder-report', type: 'Part Finder', workflow: 'Search' },
];

const summarizeFailures = (
  enabledKinds: FailureKind[],
  countsByKind?: Record<FailureKind, number>,
  mostRecentByKind?: Partial<Record<FailureKind, string>>
): FailureKindSummary[] =>
  FAILURE_KINDS.filter(({ kind }) => enabledKinds.includes(kind)).map(definition => ({
    ...definition,
    count: countsByKind?.[definition.kind] ?? 0,
    mostRecent: mostRecentByKind?.[definition.kind],
  }));

export const RecentFailuresTile = () => {
  const { isPartFinderEnabled } = useFeatureFlags();
  const { countsByKind, mostRecentByKind, isLoading, error } = useGetRecentFailures();

  const enabledKinds: FailureKind[] = isPartFinderEnabled
    ? ['asset', 'report', 'part-finder-report']
    : ['asset', 'report'];
  const summaries = summarizeFailures(enabledKinds, countsByKind, mostRecentByKind);
  const hasAnyFailure = summaries.some(summary => summary.count > 0);

  return (
    <DashboardTile id='recent-failures' title='Needs Attention' icon={<ReportProblemOutlined />}>
      {error && <BodyText color='text.secondary'>Failed to load recent failures.</BodyText>}
      {!error && isLoading && <Skeleton variant='rounded' height={160} />}
      {!error && !isLoading && <FailureSummaryTable summaries={summaries} />}
      {!error && !isLoading && !hasAnyFailure && (
        <TileEmptyState
          testId='no-recent-failures'
          tone='positive'
          graphic={<CheckCircleOutline />}
          title='All clear'
          description='Nothing has failed recently. Failed uploads, reports and lookups would show here.'
        />
      )}
    </DashboardTile>
  );
};
